import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React from "react";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { ModeInsightsProps } from "../../types/insights";
import ModeInsightsCard from "./ModeInsightsCard";
import StarIcon from "./StarIcon";

export default function LoanAmountInsights({ model }: ModeInsightsProps) {
  const c = useCurrencyFormatter();
  const nSimulations = model.results.length;

  const maxPrincipal = Math.max(
    ...model.results.simulations.map((sim) => sim.principal),
  );
  const minPrincipal = Math.min(
    ...model.results.simulations.map((sim) => sim.principal),
  );

  const cheapestSimulation = model.results.simulations.reduce(
    (cheapest, sim) => {
      const simTotalCost = sim.totalCostOfMortgage;
      const cheapestTotalCost = cheapest.totalCostOfMortgage;
      return simTotalCost < cheapestTotalCost ? sim : cheapest;
    },
    model.results.simulations[0],
  );

  // Calculate cost per unit borrowed for each simulation
  const simulationsWithMetrics = model.results.simulations.map((sim) => {
    const costPerUnit = sim.totalCostOfMortgage / sim.principal;
    const savingsPercentage = (
      (sim.totalInterestEarned / sim.totalInterestPaid) *
      100
    ).toFixed(1);
    return {
      sim,
      costPerUnit,
      savingsPercentage,
    };
  });

  // Sort by cost per unit (best value first)
  const sortedByValue = [...simulationsWithMetrics].sort(
    (a, b) => a.costPerUnit - b.costPerUnit,
  );

  const bestValue = sortedByValue[0];
  const worstValue = sortedByValue[sortedByValue.length - 1];
  const valueDifference =
    ((worstValue.costPerUnit - bestValue.costPerUnit) /
      worstValue.costPerUnit) *
    100;

  // Check if all simulations have the same cost per unit
  const allSameCostPerUnit =
    Math.abs(bestValue.costPerUnit - worstValue.costPerUnit) < 0.01;

  const renderValueInsight = () => {
    if (allSameCostPerUnit) {
      return (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          All loan amounts have the same cost of{" "}
          <strong>
            {c(bestValue.costPerUnit, true)} per {c(1, true)} borrowed
          </strong>
          , so there is no difference in value between options.
        </Typography>
      );
    }
    return (
      <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
        The best value loan pays{" "}
        <strong>
          {c(bestValue.costPerUnit, true)} per {c(1, true)} borrowed
        </strong>
        , which is <strong>{valueDifference.toFixed(1)}% less</strong> than the
        worst value option.
      </Typography>
    );
  };

  return (
    <ModeInsightsCard>
      <Typography variant="body2" gutterBottom>
        <strong>{nSimulations}</strong> simulations were compared with loan
        amounts ranging from <strong>{c(minPrincipal, true)}</strong> to{" "}
        <strong>{c(maxPrincipal, true)}</strong>.
      </Typography>
      <Typography variant="body2" gutterBottom>
        The most cost-effective loan amount was{" "}
        <strong>{c(cheapestSimulation.principal, true)}</strong> with a total
        cost of <strong>{c(cheapestSimulation.totalCostOfMortgage)}</strong>.
      </Typography>
      {renderValueInsight()}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Loan Amount</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Total Cost</strong>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Total cost of the mortgage divided by the loan amount">
                  <strong>Cost per {c(1, true)} Borrowed</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <strong>Total Interest</strong>
              </TableCell>
              {model.results.simulations.some(
                (sim) => sim.totalInterestEarned > 0,
              ) && (
                <TableCell align="right">
                  <Tooltip title="% of mortgage interest offset by savings & investments interest">
                    <strong>Savings Offset</strong>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedByValue.map(({ sim, costPerUnit, savingsPercentage }) => (
              <TableRow
                key={sim.principal}
                sx={{
                  backgroundColor:
                    sim === bestValue.sim
                      ? "success.lighter"
                      : sim === worstValue.sim
                        ? "error.lighter"
                        : "inherit",
                }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {c(sim.principal, true)}
                  {(sim === bestValue.sim || allSameCostPerUnit) && (
                    <StarIcon />
                  )}
                </TableCell>
                <TableCell align="right">
                  {c(sim.totalCostOfMortgage)}
                </TableCell>
                <TableCell align="right">{c(costPerUnit)}</TableCell>
                <TableCell align="right">{c(sim.totalInterestPaid)}</TableCell>
                {model.results.simulations.some(
                  (s) => s.totalInterestEarned > 0,
                ) && (
                  <TableCell align="right">
                    {sim.totalInterestEarned > 0
                      ? `${savingsPercentage}%`
                      : "-"}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ModeInsightsCard>
  );
}
